import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { raiseRiskApi } from "./api/raiseRiskApi";
import { riskDetailsApi } from "./api/riskDetailsApi";
import { queryClient } from "../../shared/providers/QueryProvider";

// create / edit risk mutation

export const useCreateRisk = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ data }: { data: any }) => {
            return await raiseRiskApi.createRisk({ data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        },
    });
};

export const useEditRisk = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, data }: { riskId: string, data: any }) => {
            return await raiseRiskApi.editRisk({ riskId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        },
    });
};

// create / edit cause mutation

export const useCreateCause = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, data }: { riskId: string, data: any }) => {
            return await raiseRiskApi.createCause({ riskId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        },
    });
};

export const useEditCause = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, causeId, data }: { riskId: string, causeId: string, data: any }) => {
            return await raiseRiskApi.editCause({ riskId, causeId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        },
    });
};

// create / edit assessment mutation

export const useCreateAssessment = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, data }: { riskId: string, data: any }) => {
            return await raiseRiskApi.createAssessmement({ riskId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
            await qc.refetchQueries({ queryKey: ["project-risk-matrix"] });
        }
    });
};

export const useEditAssessment = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, assessmentId, data }: { riskId: string, assessmentId: string, data: any }) => {
            return await raiseRiskApi.editAssessmement({ riskId, assessmentId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
            await qc.refetchQueries({ queryKey: ["project-risk-matrix"] });
        }
    });
};

// create / edit mitigation mutation

export const useCreateMitigation = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, data }: { riskId: string, data: any }) => {
            return await raiseRiskApi.createMitigation({ riskId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        }
    });
};

export const useEditMitigation = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ riskId, mitigationId, data }: { riskId: string, mitigationId: string, data: any }) => {
            return await raiseRiskApi.editMitigation({ riskId, mitigationId, data });
        },
        onSuccess: async () => {
            await qc.refetchQueries({ queryKey: ["live-progress-risks"] });
        }
    });
};

// fetching hooks

export const useCriticalRisks = (projectId: string | undefined) => {
    const { data, isFetching, isPending, isError, error } = useQuery({
        queryKey: ["project-critical-risks", projectId],
        queryFn: () => riskDetailsApi.fetchCriticalRisks(projectId)
    });
    return {
        criticalRiskData: data?.data,
        isPending, isFetching, isError, error,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["project-critical-risks"] }); }
    }
};

export const useRiskCategories = (projectId: string | undefined) => {
    const { data, isFetching, isPending, isError, error } = useQuery({
        queryKey: ["project-risk-categories", projectId],
        queryFn: () => riskDetailsApi.fetchRiskCategories(projectId)
    });
    return {
        riskCategoryData: data?.data || [],
        isPending, isFetching, isError, error,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["project-risk-categories"] }); }
    }
};

export const useRiskMatrix = (projectId: string | undefined) => {
    const { data, isFetching, isPending, isError, error } = useQuery({
        queryKey: ["project-risk-matrix", projectId],
        queryFn: () => riskDetailsApi.fetchRiskMatrix(projectId)
    });
    return {
        riskMatrixData: data?.data,
        isPending, isFetching, isError, error,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["project-risk-matrix"] }); }
    }
};

export const useRiskSummary = (projectId: string | undefined) => {
    const { data, isFetching, isPending, isError, error } = useQuery({
        queryKey: ["project-risk-summary", projectId],
        queryFn: () => riskDetailsApi.fetchRiskSummary(projectId)
    });
    return {
        riskSummaryData: data?.data,
        isPending, isFetching, isError, error,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["project-risk-summary"] }); }
    }
};

export const useFetchRisk = ({ riskId }: { riskId: string | null }) => {
    const normalizedId = riskId === 'null' ? null : riskId;
    const { data, isFetching, isPending, isError, refetch } = useQuery({
        queryKey: ["project-risk-details", riskId],
        queryFn: () => riskDetailsApi.fetchRiskDetail({ riskId: normalizedId }),
        enabled: !!normalizedId
    });
    return {
        riskData: data?.data,
        isRiskPending: isPending,
        isFetching, isError,
        refetch: refetch
    }
};

export const useRiskRegister = ({ projectId, searchKey, assignedToMe, quickFilters, status, category }: { projectId: string | undefined, searchKey?: string, assignedToMe?: boolean, quickFilters?: any, status?: string, category?: string }) => {
    const { data, isFetching, isPending, isRefetching, isError, refetch } = useQuery({
        queryKey: ["project-risk-register", projectId, searchKey, assignedToMe, quickFilters?.workstream, quickFilters?.site, quickFilters?.programme, quickFilters?.region, quickFilters?.['worktype-group'], quickFilters?.projectYear, status, category],
        queryFn: () => {
            return riskDetailsApi.fetchRiskRegister({
                projectId,
                searchKey,
                assignedToMe,
                quickFilters,
                status,
                category
            })
        }
    });

    return {
        isFetching,
        isError,
        refetch: refetch,
        riskRegisterData: data?.data,
        isPending: isPending
    };

};